/**
 * `useAiQuiz` — hook che governa lo svolgimento di un quiz AI (condotto da AIgor)
 * su una singola Skill.
 *
 * Responsabilità (design.md, sezione "Frontend"):
 *  - avvia il quiz con `POST /api/assessments/me/:skillId/ai/start` e conserva le
 *    domande, l'indice della domanda corrente e la deadline (`expiresAt`);
 *  - presenta le domande **una alla volta**: `answer(selectedIndex)` registra la
 *    risposta per la domanda corrente e avanza a quella successiva (Req. 3.2, 3.4);
 *  - mantiene un timer che espone il tempo residuo (`remainingMs`) e, alla
 *    scadenza mentre il quiz è in corso, **forza l'invio** delle sole risposte
 *    raccolte fino a quel momento (Req. 4.2);
 *  - alla risposta dell'ultima domanda invia automaticamente le risposte;
 *  - invia con `POST /api/assessments/me/:skillId/ai/submit` e, al successo,
 *    invalida la query degli assessment (`['assessments','me']`) così che la lista
 *    rifletta il nuovo `aiLevel` (Req. 6.5);
 *  - su qualunque errore di avvio o invio (incl. 409 anti-cheating/scaduto/
 *    consumato, 503 non disponibile) espone uno stato di errore così che la UI
 *    possa invitare a ripetere il quiz (Req. 8.2).
 *
 * Principio di sicurezza: il client invia **solo** gli indici scelti
 * (`selectedIndex`) per ogni domanda; nessun punteggio né risposta corretta
 * lascia mai il backend, quindi l'hook non conosce né espone le risposte esatte.
 *
 * Ordine delle opzioni: all'avvio del quiz l'hook mescola **una sola volta** le
 * opzioni di ciascuna domanda (Fisher-Yates), così l'utente non le vede sempre
 * nell'ordine restituito dall'API. Per preservare lo scoring server-side, che
 * ragiona sull'indice originale, l'hook conserva per ogni domanda la mappa
 * `indice mostrato → indice originale` e traduce la scelta prima dell'invio.
 * La UI continua a lavorare con gli indici mostrati senza conoscere il mapping.
 *
 * *Validates: Requirements 3.2, 3.4, 4.2, 8.2.*
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  AiQuizAnswer,
  AiQuizQuestionPublic,
  AiQuizSessionDto,
  AiQuizSessionStatusDto,
  AiQuizStartDto,
  AiQuizSubmitRequest,
  AssessmentView,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';

/**
 * Fase dello svolgimento del quiz:
 *  - `idle`: nessun quiz in corso (stato iniziale o dopo `reset`);
 *  - `loading`: avvio in corso (`start` in volo, prima della risposta);
 *  - `generating`: avvio accettato, le domande sono in **generazione
 *    asincrona** lato server; l'hook fa polling dello stato della sessione
 *    finché non è pronto (la UI mostra "AIgor sta preparando il quiz…");
 *  - `running`: quiz attivo, l'utente sta rispondendo alle domande;
 *  - `submitting`: invio delle risposte in corso;
 *  - `done`: quiz concluso con successo, `result` disponibile;
 *  - `error`: avvio, generazione o invio fallito, `error` valorizzato.
 */
export type AiQuizPhase =
  | 'idle'
  | 'loading'
  | 'generating'
  | 'running'
  | 'submitting'
  | 'done'
  | 'error';

/** Valore restituito da {@link useAiQuiz}. */
export interface UseAiQuizResult {
  /** Fase corrente dello svolgimento. */
  phase: AiQuizPhase;
  /** `true` mentre l'avvio del quiz è in corso (prima della risposta HTTP). */
  loading: boolean;
  /** `true` mentre le domande sono in generazione asincrona (polling in corso). */
  generating: boolean;
  /** `true` mentre il quiz è attivo e l'utente può rispondere. */
  running: boolean;
  /** `true` mentre l'invio delle risposte è in corso. */
  submitting: boolean;
  /** `true` quando il quiz si è concluso con successo (`result` disponibile). */
  done: boolean;
  /** Messaggio d'errore (avvio o invio) da mostrare all'utente, o `null`. */
  error: string | null;

  /** Skill attualmente in valutazione, o `null` se nessun quiz è in corso. */
  skillId: string | null;
  /** Domanda corrente da presentare, o `null` se non applicabile. */
  currentQuestion: AiQuizQuestionPublic | null;
  /** Indice (0-based) della domanda corrente. */
  currentIndex: number;
  /** Numero totale di domande del quiz. */
  totalQuestions: number;
  /** Millisecondi residui prima della scadenza (mai negativo). */
  remainingMs: number;
  /** Scadenza assoluta del quiz, o `null` se nessun quiz è in corso. */
  deadline: Date | null;
  /** Vista aggiornata dell'assessment (con `aiLevel`) al termine, o `null`. */
  result: AssessmentView | null;

  /** Avvia un nuovo quiz per la Skill indicata. */
  start: (skillId: string) => Promise<void>;
  /**
   * Registra la risposta per la domanda corrente e avanza. Se era l'ultima
   * domanda, invia automaticamente le risposte.
   */
  answer: (selectedIndex: number) => void;
  /**
   * Salta la domanda corrente ("Non lo so"): avanza senza registrare alcuna
   * risposta, così che lo scoring server-side la conti come omessa (0 punti).
   * All'ultima domanda invia automaticamente le risposte raccolte finora.
   */
  skip: () => void;
  /** Reimposta lo stato dell'hook (chiusura del Drawer). */
  reset: () => void;
}

/** Intervallo di aggiornamento del timer residuo (ms). */
const TICK_MS = 250;

/** Intervallo di polling dello stato della sessione durante la generazione (ms). */
const POLL_INTERVAL_MS = 2000;

/**
 * Tempo massimo di attesa della generazione prima di arrendersi (ms). Copre con
 * margine il timeout del worker lato server (~120s): oltre questo limite l'hook
 * espone un errore invitando a riprovare, invece di pollare all'infinito.
 */
const POLL_TIMEOUT_MS = 180_000;

/** Attende `ms` millisecondi (usato tra un tentativo di polling e il successivo). */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Calcola i millisecondi residui (mai negativi) fino alla scadenza. */
function computeRemaining(deadline: Date | null): number {
  if (deadline === null) return 0;
  return Math.max(0, deadline.getTime() - Date.now());
}

/** Estrae un messaggio d'errore leggibile da un valore sconosciuto. */
function messageFromError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'Si è verificato un errore imprevisto. Riprova a ripetere il quiz.';
}

/**
 * Sessione del quiz con le opzioni mescolate per la presentazione, più il
 * mapping per-domanda dall'indice mostrato all'utente all'indice originale
 * restituito dall'API. Il mapping è indispensabile per tradurre la scelta prima
 * dell'invio, dato che lo scoring server-side ragiona sull'indice originale.
 */
interface ShuffledSession {
  /** Sessione con le `options` di ogni domanda nell'ordine mostrato. */
  session: AiQuizSessionDto;
  /**
   * Per ogni `questionId`, l'array `originalIndexByDisplayIndex[i]` è l'indice
   * originale (lato API) dell'opzione mostrata in posizione `i`.
   */
  originalIndexByQuestionId: Map<string, number[]>;
}

/**
 * Restituisce una permutazione degli indici `0..length-1` tramite Fisher-Yates,
 * usando `Math.random` (l'ordine di presentazione non ha requisiti crittografici).
 */
function shuffledIndices(length: number): number[] {
  const indices = Array.from({ length }, (_, i) => i);
  for (let i = length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices;
}

/**
 * Mescola le opzioni di ogni domanda della sessione e produce, per ciascuna, la
 * mappa `indice mostrato → indice originale`. Non muta l'input.
 */
function shuffleSessionOptions(dto: AiQuizSessionDto): ShuffledSession {
  const originalIndexByQuestionId = new Map<string, number[]>();
  const questions = dto.questions.map((question) => {
    const order = shuffledIndices(question.options.length);
    originalIndexByQuestionId.set(question.id, order);
    return {
      ...question,
      options: order.map((originalIndex) => question.options[originalIndex]),
    };
  });
  return {
    session: { ...dto, questions },
    originalIndexByQuestionId,
  };
}

/**
 * Hook di stato per il quiz AI. Un'unica istanza governa un quiz alla volta
 * (tipicamente montato dal Drawer di AIgor).
 */
export function useAiQuiz(): UseAiQuizResult {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<AiQuizPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [skillId, setSkillId] = useState<string | null>(null);
  const [session, setSession] = useState<AiQuizSessionDto | null>(null);
  const [answers, setAnswers] = useState<AiQuizAnswer[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [result, setResult] = useState<AssessmentView | null>(null);

  // Ref alle risposte correnti: consente al submit forzato dal timer di leggere
  // sempre l'ultimo valore senza dipendere dalle chiusure di `useEffect`.
  const answersRef = useRef<AiQuizAnswer[]>(answers);
  answersRef.current = answers;

  // Ref di guardia: evita invii concorrenti (timer + ultima risposta) sulla
  // stessa sessione.
  const submittingRef = useRef(false);

  // Token monotono dell'avvio corrente: incrementato ad ogni `start`/`reset`.
  // Il loop di polling confronta il proprio token con questo per capire se è
  // stato "sorpassato" (nuovo avvio, chiusura del Drawer) e interrompersi senza
  // applicare risultati obsoleti a una sessione non più attiva.
  const startTokenRef = useRef(0);

  // Mapping per-domanda dall'indice mostrato all'utente (opzioni mescolate) a
  // quello originale restituito dall'API. Popolato in `start`, letto in `answer`
  // per tradurre la scelta prima dell'invio al backend.
  const originalIndexByQuestionIdRef = useRef<Map<string, number[]>>(new Map());

  /**
   * Invia le risposte raccolte per la sessione indicata. Idempotente rispetto a
   * invii concorrenti (timer/ultima risposta) grazie a `submittingRef`.
   */
  const submit = useCallback(
    async (targetSkillId: string, sessionId: string, collected: AiQuizAnswer[]) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setPhase('submitting');
      try {
        const body: AiQuizSubmitRequest = { sessionId, answers: collected };
        const view = await api.post<AssessmentView>(
          `/api/assessments/me/${encodeURIComponent(targetSkillId)}/ai/submit`,
          body,
        );
        setResult(view);
        setPhase('done');
        // Riflette il nuovo aiLevel nella lista degli assessment.
        void queryClient.invalidateQueries({ queryKey: ['assessments', 'me'] });
        // Il nuovo aiLevel concorre al rankScore: invalida anche il dashboard
        // così il donut del rank nel dettaglio competenza si aggiorna subito,
        // come già avviene per il salvataggio del Self inline.
        void queryClient.invalidateQueries({ queryKey: ['dashboard', 'me'] });
      } catch (err) {
        setError(messageFromError(err));
        setPhase('error');
      } finally {
        submittingRef.current = false;
      }
    },
    [api, queryClient],
  );

  /**
   * Prepara lo stato "running" a partire da una sessione pronta (`ready`):
   * mescola le opzioni una sola volta, conserva la mappa indice mostrato →
   * indice originale, avvia il timer ed entra in fase `running`.
   */
  const beginRunning = useCallback((ready: AiQuizSessionDto) => {
    const { session: shuffled, originalIndexByQuestionId } =
      shuffleSessionOptions(ready);
    originalIndexByQuestionIdRef.current = originalIndexByQuestionId;
    const dl = new Date(ready.expiresAt);
    setSession(shuffled);
    setDeadline(dl);
    setRemainingMs(computeRemaining(dl));
    setPhase('running');
  }, []);

  /**
   * Avvia un nuovo quiz. L'avvio è **asincrono**: l'endpoint `/ai/start` crea
   * una sessione e ritorna subito `pending` (domande in generazione) oppure
   * `ready` (domande già disponibili, es. riuso). Se `pending`, l'hook entra in
   * fase `generating` e fa **polling** di `/ai/sessions/:sessionId` finché lo
   * stato non diventa `ready` (avvia il quiz) o `failed` (errore, si riprova).
   *
   * Il polling è protetto da un token (`startTokenRef`): un nuovo avvio o un
   * `reset` lo invalida, così i risultati di un polling obsoleto non vengono
   * applicati a una sessione non più attiva.
   */
  const start = useCallback(
    async (targetSkillId: string) => {
      submittingRef.current = false;
      const token = startTokenRef.current + 1;
      startTokenRef.current = token;

      setPhase('loading');
      setError(null);
      setResult(null);
      setAnswers([]);
      setCurrentIndex(0);
      setSkillId(targetSkillId);
      setSession(null);
      setDeadline(null);

      const basePath = `/api/assessments/me/${encodeURIComponent(targetSkillId)}/ai`;

      try {
        const startDto = await api.post<AiQuizStartDto>(`${basePath}/start`);
        // Un nuovo avvio/reset è intervenuto nel frattempo: abbandona.
        if (startTokenRef.current !== token) return;

        // Domande già pronte (riuso): parti subito, senza polling.
        if (startDto.status === 'ready' && startDto.session !== undefined) {
          beginRunning(startDto.session);
          return;
        }

        // Generazione in corso: entra in fase `generating` e fai polling.
        setPhase('generating');
        const startedAt = Date.now();
        // Prima di ripollare attende l'intervallo: la generazione richiede
        // comunque qualche secondo, inutile interrogare immediatamente.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (startTokenRef.current !== token) return;
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            setError(
              'La generazione del quiz sta richiedendo troppo tempo. Riprova tra poco.',
            );
            setPhase('error');
            return;
          }
          await delay(POLL_INTERVAL_MS);
          if (startTokenRef.current !== token) return;

          const status = await api.get<AiQuizSessionStatusDto>(
            `${basePath}/sessions/${encodeURIComponent(startDto.sessionId)}`,
          );
          if (startTokenRef.current !== token) return;

          if (status.status === 'ready' && status.session !== undefined) {
            beginRunning(status.session);
            return;
          }
          if (status.status === 'failed') {
            setError(
              'AIgor non è riuscito a preparare il quiz. Riprova a ripetere il quiz.',
            );
            setPhase('error');
            return;
          }
          // `pending`: continua a pollare.
        }
      } catch (err) {
        if (startTokenRef.current !== token) return;
        setError(messageFromError(err));
        setPhase('error');
      }
    },
    [api, beginRunning],
  );

  /**
   * Registra la risposta per la domanda corrente e avanza. All'ultima domanda
   * invia automaticamente le risposte (Req. 3.4, 3.6).
   */
  const answer = useCallback(
    (displayedIndex: number) => {
      if (phase !== 'running' || session === null || skillId === null) return;
      const question = session.questions[currentIndex];
      if (!question) return;

      // La UI passa l'indice nell'ordine mostrato (mescolato): lo traduco
      // nell'indice originale atteso dallo scoring server-side. Se il mapping
      // mancasse (caso non atteso), uso l'indice così com'è come fallback.
      const order = originalIndexByQuestionIdRef.current.get(question.id);
      const selectedIndex = order?.[displayedIndex] ?? displayedIndex;

      const next: AiQuizAnswer[] = [
        ...answersRef.current.filter((a) => a.questionId !== question.id),
        { questionId: question.id, selectedIndex },
      ];
      setAnswers(next);

      const isLast = currentIndex >= session.questions.length - 1;
      if (isLast) {
        void submit(skillId, session.sessionId, next);
      } else {
        setCurrentIndex((i) => i + 1);
      }
    },
    [phase, session, skillId, currentIndex, submit],
  );

  /**
   * Salta la domanda corrente ("Non lo so"): NON registra alcuna risposta per
   * la domanda (rimuove un'eventuale risposta precedente, per idempotenza) e
   * avanza. Lo scoring server-side, trovando `questionId` senza risposta, la
   * conta come omessa (0 punti), esattamente come una domanda non raggiunta
   * (Req. 5.1, 5.3). All'ultima domanda invia le risposte raccolte finora.
   */
  const skip = useCallback(() => {
    if (phase !== 'running' || session === null || skillId === null) return;
    const question = session.questions[currentIndex];
    if (!question) return;

    // Nessuna risposta per questa domanda: eventuale scelta precedente rimossa.
    const next: AiQuizAnswer[] = answersRef.current.filter(
      (a) => a.questionId !== question.id,
    );
    setAnswers(next);

    const isLast = currentIndex >= session.questions.length - 1;
    if (isLast) {
      void submit(skillId, session.sessionId, next);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }, [phase, session, skillId, currentIndex, submit]);

  /** Reimposta lo stato dell'hook. */
  const reset = useCallback(() => {
    submittingRef.current = false;
    // Invalida un eventuale polling in corso: il loop confronterà il token e si
    // interromperà senza applicare risultati obsoleti.
    startTokenRef.current += 1;
    originalIndexByQuestionIdRef.current = new Map();
    setPhase('idle');
    setError(null);
    setSkillId(null);
    setSession(null);
    setAnswers([]);
    setCurrentIndex(0);
    setDeadline(null);
    setRemainingMs(0);
    setResult(null);
  }, []);

  // Timer del quiz: aggiorna il tempo residuo e, alla scadenza mentre il quiz è
  // in corso, forza l'invio delle sole risposte raccolte (Req. 4.2). Pulizia
  // dell'intervallo allo smontaggio e al termine del quiz.
  useEffect(() => {
    if (phase !== 'running' || deadline === null) return;

    const tick = () => {
      const remaining = computeRemaining(deadline);
      setRemainingMs(remaining);
      if (remaining <= 0 && skillId !== null && session !== null) {
        void submit(skillId, session.sessionId, answersRef.current);
      }
    };

    tick();
    const timerId = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(timerId);
  }, [phase, deadline, skillId, session, submit]);

  const currentQuestion = useMemo<AiQuizQuestionPublic | null>(() => {
    if (session === null) return null;
    return session.questions[currentIndex] ?? null;
  }, [session, currentIndex]);

  return {
    phase,
    loading: phase === 'loading',
    generating: phase === 'generating',
    running: phase === 'running',
    submitting: phase === 'submitting',
    done: phase === 'done',
    error: phase === 'error' ? error : null,
    skillId,
    currentQuestion,
    currentIndex,
    totalQuestions: session?.questions.length ?? 0,
    remainingMs,
    deadline,
    result,
    start,
    answer,
    skip,
    reset,
  };
}
